import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import OutlookCalendarCard from "../components/OutlookCalendarCard";

/**
 * Family calendar. With Outlook connected (card at the top), Outlook is the master copy: its events appear here
 * by themselves and anything added or changed here is saved to Outlook. Kids see their own events in the app's
 * "What's on" screen. Backend: /api/events, /api/calendar/*.
 */
// Microsoft's reasons for refusing Yoda's sign-in code, in words a parent can act on (the fix is in Azure).
const OUTLOOK_REASONS = {
  AADSTS7000215: "Microsoft says Yoda's client secret is wrong. In the Web App, OUTLOOK_CLIENT_SECRET must be the secret's Value (not its Secret ID).",
  AADSTS7000222: "Microsoft says Yoda's client secret has expired. Make a new one in the app registration and put its Value in OUTLOOK_CLIENT_SECRET.",
  AADSTS700016: "Microsoft doesn't know Yoda's app. Check OUTLOOK_CLIENT_ID is the app registration's Application (client) ID.",
  AADSTS50011: "The return address doesn't match. Add https://yoda.watch/api/calendar/outlook/callback under Web in the app registration's Authentication.",
  AADSTS500113: "The return address doesn't match. Add https://yoda.watch/api/calendar/outlook/callback under Web in the app registration's Authentication.",
  AADSTS9002327: "The return address is set up as a single-page app. Move it to Web in the app registration's Authentication.",
  AADSTS700025: "The app registration is set up as a public app. Put the return address under Web and turn off Allow public client flows.",
  AADSTS70000: "The sign-in took too long or was used twice. Please try again.",
  AADSTS54005: "The sign-in was used twice. Please try again.",
};

function outlookFailure(why) {
  const base = "Outlook sign-in didn't work.";
  if (!why) return `${base} Please try again.`;
  return `${base} ${OUTLOOK_REASONS[why] || "Please try again."} (Microsoft code ${why})`;
}

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | eventObject
  const [who, setWho] = useState("all"); // "all" | a kid's id | "parents": whose events the month shows

  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    const r = q.get("outlook");
    if (r === "connected") toast.success("Outlook connected. Your events are on their way in.");
    else if (r === "failed") toast.error(outlookFailure(q.get("why")), { duration: 30000 });
    if (r) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [ev, ks] = await Promise.all([api.get("/events"), api.get("/kids")]);
      setEvents(ev.data);
      setKids(ks.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }

  const today = new Date();
  // What the page is looking at: a day, the week holding it, or its month. Opens on this week.
  const [mode, setMode] = useState("week"); // "day" | "week" | "month"
  const [focus, setFocus] = useState(() => startOfDay(new Date()));

  const shown = useMemo(() => events.filter((e) => {
    if (who === "all") return true;
    if (who === "parents") return e.audience === "parents";
    return e.audience === "everyone" || (e.kid_ids || []).includes(who);
  }), [events, who]);

  // Every event on one date, in start order. Yoda's weekly events land on each same weekday from their start.
  const byDay = useMemo(() => {
    const map = {};
    const weekly = [];
    for (const e of shown) {
      if (!e.start_at) continue;
      if (REPEATS[e.recurrence] && e.source !== "outlook") { weekly.push(e); continue; }
      (map[dayKey(new Date(e.start_at))] ||= []).push(e);
    }
    return { map, weekly };
  }, [shown]);

  function eventsOn(d) {
    const out = [...(byDay.map[dayKey(d)] || [])];
    for (const e of byDay.weekly) {
      const s = new Date(e.start_at);
      if (repeatsOn(e.recurrence, s, d)) {
        const start = new Date(d); start.setHours(s.getHours(), s.getMinutes(), 0, 0);
        const end = new Date(start.getTime() + (new Date(e.end_at) - s));
        out.push({ ...e, start_at: start.toISOString(), end_at: end.toISOString() });
      }
    }
    return out.sort((a, b) => (b.all_day - a.all_day) || (new Date(a.start_at) - new Date(b.start_at)));
  }

  function move(step) {
    const d = new Date(focus);
    if (mode === "day") d.setDate(d.getDate() + step);
    else if (mode === "week") d.setDate(d.getDate() + 7 * step);
    else { d.setDate(1); d.setMonth(d.getMonth() + step); }
    setFocus(d);
  }

  const weekStart = useMemo(() => {
    const d = startOfDay(focus);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
    return d;
  }, [focus]);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });

  const cells = useMemo(() => {
    const first = new Date(focus.getFullYear(), focus.getMonth(), 1);
    const last = new Date(focus.getFullYear(), focus.getMonth() + 1, 0);
    const out = [];
    for (let i = 0; i < (first.getDay() + 6) % 7; i++) out.push(null);
    for (let d = 1; d <= last.getDate(); d++) out.push(new Date(focus.getFullYear(), focus.getMonth(), d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [focus.getFullYear(), focus.getMonth()]);

  const heading = mode === "day"
    ? focus.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })
    : mode === "week"
      ? `${weekDays[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
      : focus.toLocaleString(undefined, { month: "long", year: "numeric" });
  const isToday = (d) => d.toDateString() === today.toDateString();

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Calendar</h1>
          <p className="text-sm text-[#707A6E] mt-1">School, sport and everything else the kids have on. They see their own events in Yoda Watch.</p>
        </div>
        <button
          data-testid="add-event-btn"
          onClick={() => setEditing("new")}
          className="rounded-xl bg-[#2C342A] px-4 py-2 text-sm font-medium text-[#F5F4F0] hover:bg-[#2C342A]"
        >
          + Add event
        </button>
      </div>

      <OutlookCalendarCard kids={kids} onChanged={load} />

      <div className="flex flex-wrap gap-2" data-testid="calendar-who">
        {[{ id: "all", name: "Everything" }, ...kids.map((k) => ({ id: k.id, name: k.name })), { id: "parents", name: "Just us" }].map((o) => (
          <button key={o.id} onClick={() => setWho(o.id)} data-testid={`calendar-who-${o.id}`}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium border ${who === o.id
                    ? "bg-[#2C342A] text-[#F5F4F0] border-[#2C342A]" : "bg-white text-[#2C342A] border-[#E4E2DC] hover:bg-[#F0EFEB]"}`}>
            {o.name}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-[#FAFAF7] border border-[#F0EFEB] p-4 md:p-5">
        <div className="mb-4 flex rounded-[13px] bg-[#E8E6DF] p-[3px] max-w-md">
          {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`calendar-mode-${id}`}
                    className={`flex-1 rounded-[10px] py-2 text-sm font-semibold ${mode === id ? "bg-white text-[#2C342A] shadow-sm" : "text-[#707A6E] hover:text-[#2C342A]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between mb-4">
          <button data-testid="prev-month" onClick={() => move(-1)} className="rounded-lg px-3 py-1.5 hover:bg-[#F0EFEB]">‹</button>
          <div className="text-center">
            <div className="font-heading text-xl">{heading}</div>
            <button onClick={() => setFocus(startOfDay(new Date()))} className="text-xs text-[#707A6E] hover:underline">Today</button>
          </div>
          <button data-testid="next-month" onClick={() => move(1)} className="rounded-lg px-3 py-1.5 hover:bg-[#F0EFEB]">›</button>
        </div>

        {mode === "day" && (
          <DayList events={eventsOn(focus)} kids={kids} onOpen={setEditing} empty="Nothing on this day 🌿" />
        )}

        {mode === "week" && (
          <div className="space-y-2" data-testid="calendar-week">
            {weekDays.map((d) => {
              const evs = eventsOn(d);
              return (
                <div key={dayKey(d)} className={`flex gap-3 rounded-2xl bg-white p-3 ${isToday(d) ? "ring-2 ring-[#3E98D8]" : "border border-[#F0EFEB]"}`}>
                  <button onClick={() => { setFocus(d); setMode("day"); }} className="w-12 flex-none text-center">
                    <div className="text-[11px] font-bold tracking-wider text-[#707A6E] uppercase">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className="text-2xl font-bold text-[#2C342A]">{d.getDate()}</div>
                  </button>
                  <div className="flex-1 min-w-0">
                    <DayList events={evs} kids={kids} onOpen={setEditing} empty="Nothing on 🌿" compact />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mode === "month" && (
          <>
            <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-wider text-[#707A6E] mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} className="min-h-[80px]"></div>;
                const key = dayKey(cell);
                const dayEvents = eventsOn(cell);
                const picked = cell.toDateString() === focus.toDateString();
                return (
                  <div key={i} onClick={() => setFocus(cell)}
                       className={`min-h-[80px] cursor-pointer rounded-lg border p-1.5 ${isToday(cell) ? "border-[#2C342A] bg-[#FAFAF7]" : "border-[#F0EFEB] bg-white/60"} ${picked ? "ring-2 ring-[#3E98D8]" : ""}`}
                       data-testid={`cal-day-${key}`}>
                    <div className={`text-xs mb-1 ${isToday(cell) ? "font-bold text-[#2C342A]" : "text-[#707A6E]"}`}>{cell.getDate()}</div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((e, ix) => (
                        <div key={ix} className="w-full text-left text-[10px] rounded px-1.5 py-0.5 truncate"
                             style={{ background: (e.color || "#6B9F80") + "26", color: e.color || "#4B6640", opacity: e.audience === "parents" ? 0.55 : 1 }}
                             title={e.title} data-testid={`cal-event-${e.id}`}>
                          {e.waiting ? "⏳ " : ""}{e.emoji} {e.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[10px] text-[#707A6E]">+{dayEvents.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <div className="mb-2 text-sm font-semibold text-[#2C342A]">
                {focus.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
              </div>
              <DayList events={eventsOn(focus)} kids={kids} onOpen={setEditing} empty="Nothing on this day 🌿" />
            </div>
          </>
        )}
      </div>

      {loading && <div className="text-sm text-[#707A6E]">Loading…</div>}

      {editing && (
        <EventEditor
          initial={editing === "new" ? null : editing}
          kids={kids}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// How a Yoda event can repeat (the server's family_calendar.REPEATS).
const REPEATS = { weekly: "Every week", fortnightly: "Every 2 weeks", monthly: "Every month", yearly: "Every year" };

/** Whether an event first on `s` that repeats `kind` falls on day `d` (a short month uses its last day). */
function repeatsOn(kind, s, d) {
  const first = startOfDay(s), day = startOfDay(d);
  if (day < first) return false;
  const days = Math.round((day - first) / 86400000);
  if (kind === "weekly") return days % 7 === 0;
  if (kind === "fortnightly") return days % 14 === 0;
  const months = (day.getFullYear() - first.getFullYear()) * 12 + day.getMonth() - first.getMonth();
  if (kind === "yearly" && months % 12 !== 0) return false;
  if (kind !== "monthly" && kind !== "yearly") return false;
  const last = new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate();
  return day.getDate() === Math.min(first.getDate(), last);
}

function EventEditor({ initial, kids, onClose, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [emoji, setEmoji] = useState(initial?.emoji || "📅");
  const [color, setColor] = useState(initial?.color || "#6B9F80");
  const [location, setLocation] = useState(initial?.location || "");
  const [startAt, setStartAt] = useState(dtLocal(initial?.start_at ? new Date(initial.start_at) : new Date()));
  const [endAt, setEndAt] = useState(dtLocal(initial?.end_at ? new Date(initial.end_at) : new Date(Date.now() + 60 * 60 * 1000)));
  const [recurrence, setRecurrence] = useState(initial?.recurrence || "none");
  const [kidIds, setKidIds] = useState(initial?.kid_ids || []);
  const [parentsOnly, setParentsOnly] = useState(initial?.audience === "parents");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [remind, setRemind] = useState(initial?.remind_minutes ?? "");
  const [pauseBefore, setPauseBefore] = useState(initial?.pause_before_minutes ?? "");
  const fromOutlook = initial?.source === "outlook";
  const [saving, setSaving] = useState(false);
  const [everyHere, setEveryHere] = useState(false); // also send every event at this place to these kids
  const [outlookAccount, setOutlookAccount] = useState(null); // set when saving here changes the Outlook calendar

  useEffect(() => {
    api.get("/calendar/outlook").then(({ data }) => {
      if (data.connected && data.calendar_id) setOutlookAccount(data.account || "your Outlook");
    }).catch(() => {});
  }, []);

  async function save() {
    // New events, and a new name, time or place on an Outlook one, change the family's real Outlook calendar.
    const toOutlook = outlookAccount && (!initial?.id || (fromOutlook && (
      title !== (initial.title || "") || location !== (initial.location || "")
      || new Date(startAt).getTime() !== new Date(initial.start_at).getTime()
      || new Date(endAt).getTime() !== new Date(initial.end_at).getTime())));
    if (toOutlook && !window.confirm(`This ${initial?.id ? "changes" : "adds"} "${title}" in your Outlook calendar (${outlookAccount}). Everyone who uses that calendar will see it.\n\nSave to Outlook?`)) return;
    setSaving(true);
    try {
      const body = {
        title, emoji, color, location, notes,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        kid_ids: kidIds,
        audience: parentsOnly ? "parents" : (kidIds.length ? "kids" : "everyone"),
      };
      if (!fromOutlook) body.recurrence = recurrence;
      if (remind === "") body.clear_remind = true; else body.remind_minutes = Number(remind);
      if (pauseBefore === "") body.clear_pause = true; else body.pause_before_minutes = Number(pauseBefore);
      if (!initial?.id) { delete body.clear_remind; delete body.clear_pause; }
      // An Outlook event keeps following its rules (name, place, word) unless who's going was actually changed here.
      if (fromOutlook && body.audience === initial.audience
          && [...kidIds].sort().join() === [...(initial.kid_ids || [])].sort().join()) {
        delete body.audience; delete body.kid_ids;
      }
      if (initial?.id) await api.patch(`/events/${initial.event_id || initial.id}`, body);
      else await api.post("/events", body);
      const place = location.trim();
      if (everyHere && place && !parentsOnly) {
        const { data: st } = await api.get("/calendar/outlook");
        const rest = (st.rules || []).filter((r) => !(r.kind === "location" && r.value.toLowerCase() === place.toLowerCase()));
        await api.put("/calendar/settings", { rules: [...rest, { kind: "location", value: place, kid_ids: kidIds.length ? kidIds : ["all"] }] });
      }
      toast.success(initial?.id ? "Event updated" : "Event created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!initial?.id) return;
    if (!confirm(fromOutlook ? "Delete this event from Outlook too?" : "Delete this event?")) return;
    setSaving(true);
    try {
      await api.delete(`/events/${initial.event_id || initial.id}`);
      toast.success("Event deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-[#FAFAF7] p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="event-editor">
        <h3 className="font-heading text-xl">{initial?.id ? "Edit event" : "New event"}</h3>
        {outlookAccount && (!initial?.id || fromOutlook) && (
          <p className="rounded-lg bg-[#EAF2FA] px-3 py-2 text-xs text-[#2F5F86]" data-testid="event-outlook-warning">
            📅 Saving a new name, time or place here changes your Outlook calendar ({outlookAccount}) too.
          </p>
        )}
        {fromOutlook && (
          <p className="text-xs text-[#707A6E] -mt-2">From Outlook. Title, time and place save back to Outlook (this date
            only). Who's going, the reminder and the note apply to every date of this event.</p>
        )}
        <div className="grid grid-cols-[auto,1fr,auto] gap-2 items-center">
          <input value={emoji} onChange={(e) => setEmoji(e.target.value)} className="w-16 rounded-lg border border-[#F0EFEB] px-3 py-2 text-center bg-white" data-testid="event-emoji" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Football training)"
                 className="rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-title" />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-16 rounded-lg cursor-pointer" data-testid="event-color" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <div className="text-[#707A6E] mb-1">Starts</div>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
                   className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-start" />
          </label>
          <label className="text-sm">
            <div className="text-[#707A6E] mb-1">Ends</div>
            <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)}
                   className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-end" />
          </label>
        </div>
        <label className="text-sm block">
          <div className="text-[#707A6E] mb-1">Location (optional)</div>
          <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-location" />
        </label>
        {!fromOutlook && <label className="text-sm block">
          <div className="text-[#707A6E] mb-1">Repeats</div>
          <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-recurrence">
            <option value="none">Once</option>
            <option value="weekly">Every week (same day)</option>
            <option value="fortnightly">Every 2 weeks</option>
            <option value="monthly">Every month (same date)</option>
            <option value="yearly">Every year (birthdays)</option>
          </select>
        </label>}
        <div className="text-sm">
          <div className="text-[#707A6E] mb-1">Who it applies to</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setKidIds([]); setParentsOnly(false); }}
                    className={`rounded-full px-3 py-1 text-xs ${kidIds.length === 0 && !parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="event-kid-all">All kids</button>
            {kids.map((k) => (
              <button key={k.id}
                      onClick={() => { setParentsOnly(false); setKidIds((prev) => prev.includes(k.id) ? prev.filter(x => x !== k.id) : [...prev, k.id]); }}
                      className={`rounded-full px-3 py-1 text-xs ${kidIds.includes(k.id) && !parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                      data-testid={`event-kid-${k.id}`}>{k.name}</button>
            ))}
            <button onClick={() => { setParentsOnly(true); setKidIds([]); }}
                    className={`rounded-full px-3 py-1 text-xs ${parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="event-parents-only">Parents only</button>
          </div>
          {location.trim() && !parentsOnly && (
            <label className="mt-2 flex items-start gap-2 text-xs text-[#707A6E]" data-testid="event-every-here">
              <input type="checkbox" checked={everyHere} onChange={(e) => setEveryHere(e.target.checked)} className="mt-0.5" />
              <span>Every event at 📍 {location.trim()} goes to {kidIds.length ? kids.filter((k) => kidIds.includes(k.id)).map((k) => k.name).join(", ") : "all the kids"}, from now on</span>
            </label>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label>
            <div className="text-[#707A6E] mb-1">Remind the kids</div>
            <select value={remind} onChange={(e) => setRemind(e.target.value)} data-testid="event-remind"
                    className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white">
              <option value="">No reminder</option>
              {[5, 10, 15, 30, 60, 120].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? "s" : ""}`} before</option>)}
            </select>
          </label>
          <label>
            <div className="text-[#707A6E] mb-1">Screens off</div>
            <select value={pauseBefore} onChange={(e) => setPauseBefore(e.target.value)} data-testid="event-pause-before"
                    className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white">
              <option value="">Don't switch off</option>
              {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} min before, until it ends</option>)}
            </select>
          </label>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note for the kids (optional), e.g. bring boots"
                  className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white text-sm" rows={2} data-testid="event-notes" />
        <div className="flex justify-between pt-2">
          <div>
            {initial?.id && (
              <button onClick={remove} disabled={saving} className="rounded-lg px-4 py-2 text-sm text-[#C06E52] hover:bg-[#F6E3DC]" data-testid="event-delete">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-[#F0EFEB]" data-testid="event-cancel">Cancel</button>
            <button onClick={save} disabled={saving || !title} className="rounded-lg bg-[#2C342A] px-4 py-2 text-sm text-[#F5F4F0]" data-testid="event-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function dayKey(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dtLocal(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function timeText(e) {
  if (e.all_day) return "All day";
  const t = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${t(new Date(e.start_at))} – ${t(new Date(e.end_at))}`;
}

function whoText(e, kids) {
  if (e.audience === "everyone") return "Everyone";
  if (e.audience === "parents") return "Just us";
  return kids.filter((k) => (e.kid_ids || []).includes(k.id)).map((k) => k.name.split(" ")[0]).join(", ");
}

/** One day's events as rows: emoji, title, then time · place · how often it repeats · who it's for. */
function DayList({ events, kids, onOpen, empty, compact }) {
  if (!events.length) return <div className={`text-sm text-[#A6ABA3] ${compact ? "py-1" : "py-3"}`}>{empty}</div>;
  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"}>
      {events.map((e, i) => (
        <button key={`${e.id}-${i}`} onClick={() => onOpen(e)}
                className={`flex w-full items-center gap-3 rounded-xl text-left ${compact ? "px-2.5 py-1.5" : "px-3 py-2.5"}`}
                style={{ background: (e.color || "#6B9F80") + "1F", opacity: e.audience === "parents" ? 0.7 : 1 }}
                data-testid={`cal-row-${e.id}`}>
          <span className={compact ? "text-lg" : "text-2xl"}>{e.emoji}</span>
          <span className="flex-1 min-w-0">
            <span className={`block truncate font-semibold text-[#2C342A] ${compact ? "text-sm" : "text-[15px]"}`}>
              {e.title}
              {e.waiting && <span className="ml-2 rounded-full bg-[#FFF1CC] px-2 py-0.5 text-[11px] font-medium text-[#7A5208]">Added by the kid, waiting for you</span>}
            </span>
            <span className="block truncate text-xs text-[#707A6E]">
              {[timeText(e), e.location, e.repeat, whoText(e, kids)].filter(Boolean).join(" · ")}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
